import React, { useState } from 'react';

import Dropdown, {
  DropdownItemCheckbox,
  DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import Spinner from '@atlaskit/spinner';

import { ErrorMessageWithRetry } from '../../error-message-with-retry';

import { FilterDropdownItemLabel } from './filter-dropdown-item-label';
import { FilterDropdownTrigger } from './filter-dropdown-trigger';
import * as styles from './filter-dropdown.styled';

type Option = {
  value: string;
  label: string;
  avatar?: React.ReactNode;
};

type MultiSelectFilterDropdownProps = {
  isLoading: boolean;
  label: string;
  options: Option[];
  optionsTitle?: string;
  tooltipText?: string;
  onChange: (value: string[]) => void;
  selected: string[];
  components?: {
    CustomFilterDropdownTrigger?: React.ReactNode;
  };
  errorMessage?: string;
  onRetry?: () => void;
};

export const MultiSelectFilterDropdown = (
  props: MultiSelectFilterDropdownProps
) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    selected,
    label,
    options,
    isLoading,
    optionsTitle,
    tooltipText,
    onChange,
    components,
    errorMessage,
    onRetry,
  } = props;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const value = e.currentTarget.id;

    if (selected.some(item => item === value)) {
      // Remove from selected
      const newSelected = selected.filter(item => item !== value);
      onChange(newSelected);
    } else {
      // Add to selected
      const newSelected = [...selected, value];
      onChange(newSelected);
    }
  };

  const handleOpenChange = ({ isOpen }: { isOpen: boolean }) => {
    setIsMenuOpen(isOpen);
  };

  const isItemSelected = (value: string) =>
    selected.some(item => item === value);

  return (
    <Dropdown
      trigger={({ triggerRef, ...dropdownProps }) =>
        components && components.CustomFilterDropdownTrigger ? (
          <span {...dropdownProps} ref={triggerRef}>
            {components.CustomFilterDropdownTrigger}
          </span>
        ) : (
          <FilterDropdownTrigger
            triggerRef={triggerRef}
            buttonProps={dropdownProps}
            selectedCount={selected.length}
            isLoading={isLoading}
            label={label}
            tooltipText={tooltipText}
            isMenuOpen={isMenuOpen}
            appearance="subtle"
          />
        )
      }
      onOpenChange={handleOpenChange}
    >
      {options.length > 0 && (
        <DropdownItemCheckboxGroup id="filer-group-title" title={optionsTitle}>
          <styles.DropdownOptionsContainer>
            {options.map(option => (
              <DropdownItemCheckbox
                key={option.value}
                id={String(option.value)}
                isSelected={isItemSelected(option.value)}
                onClick={handleClick}
              >
                <FilterDropdownItemLabel
                  avatar={option.avatar}
                  label={option.label}
                />
              </DropdownItemCheckbox>
            ))}
          </styles.DropdownOptionsContainer>
        </DropdownItemCheckboxGroup>
      )}
      {errorMessage && (
        <styles.DropdownOptionsContainerErrorLoading>
          <ErrorMessageWithRetry
            onRetry={onRetry}
            errorMessage={errorMessage}
          />
        </styles.DropdownOptionsContainerErrorLoading>
      )}
      {isLoading && (
        <styles.DropdownOptionsContainerErrorLoading>
          <Spinner size="medium" />
        </styles.DropdownOptionsContainerErrorLoading>
      )}
    </Dropdown>
  );
};
